import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { io, Socket } from 'socket.io-client'
import { v4 } from 'uuid'

import { store } from '@/app.ts'
import i18n from '@/i18n'
import { AppState, Clinician, Conversation } from '@/state/stateType.ts'
import { getActiveConversation } from '@/state/stateUtil.ts'
import { throttle } from '@/util/function.ts'
import { uploadFiles } from '@/util/storage.ts'
import { ClientToServerEvent } from '~common/clientToServerParser'
import { ServerToClientMessageParser } from '~common/serverToClientParser'

// How long to wait for the socket to connect (or reconnect) before treating
// chat as unreachable and surfacing the degraded-connection banner.
export const SOCKET_CONNECTION_TIMEOUT_MS = 5000

export type SocketConnectionStatus = 'connected' | 'connecting' | 'degraded'

export type SocketContextValue = {
  addInternalMessage: (
    internalConversationId: string,
    content: string,
    files: File[],
  ) => void
  addMessageToActiveConversation: (
    content: string,
    files: File[],
  ) => Promise<void>
  assignActiveConversation: (clinician: Clinician) => void
  changeActiveConversationQueue: (queueId: string) => void
  changeReasonForEntry: (reasonForEntryId: string) => void
  closeActiveConversation: () => void
  closeInternalConversation: (internalConversationId: string) => void
  connectionStatus: SocketConnectionStatus
  createInternalConversation: (clinician: Clinician) => void
  reconnect: () => void
  setNotification: (value?: string) => void
  socket: Socket
  throttledSendTypingIndicator: () => void
  unassignActiveConversation: () => void
}

export const SocketContext = React.createContext<SocketContextValue | null>(
  null,
)

export const SocketProvider = ({
  children,
  token,
}: {
  children: ReactNode
  token: string
}): ReactElement => {
  const registered = useRef(false)
  const degradedTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [connectionStatus, setConnectionStatus] =
    useState<SocketConnectionStatus>('connecting')

  const socket = useMemo(
    () =>
      io(import.meta.env.API_URL, {
        auth: {
          token: `Bearer ${token}`,
        },
        autoConnect: false,
        extraHeaders: {
          'Accept-Language': i18n.language,
        },
        path: '/ws',
        reconnection: true,
        transports: ['websocket'],
        withCredentials: true,
      }),
    [token],
  )

  const clearDegradedTimeout = useCallback(() => {
    clearTimeout(degradedTimeoutRef.current)
    degradedTimeoutRef.current = undefined
  }, [])

  // Chat is a non-critical dependency: give it SOCKET_CONNECTION_TIMEOUT_MS
  // to connect (or reconnect) before flagging it as degraded, instead of
  // leaving the app waiting on it forever.
  const startDegradedTimeout = useCallback(() => {
    clearDegradedTimeout()
    degradedTimeoutRef.current = setTimeout(() => {
      setConnectionStatus('degraded')
    }, SOCKET_CONNECTION_TIMEOUT_MS)
  }, [clearDegradedTimeout])

  const reconnect = useCallback(() => {
    setConnectionStatus('connecting')
    startDegradedTimeout()
    socket.connect()
  }, [socket, startDegradedTimeout])

  useEffect(() => {
    if (!token) throw new Error('No token')
    socket.connect()
    startDegradedTimeout()

    socket.on('connect', () => {
      clearDegradedTimeout()
      setConnectionStatus('connected')
    })

    socket.on('disconnect', (reason) => {
      setConnectionStatus('connecting')
      startDegradedTimeout()
      if (reason === 'io server disconnect') {
        socket.connect()
      }
    })

    socket.on('event', (event: unknown) => {
      handleEventFromServer(event)
    })

    return () => {
      clearDegradedTimeout()
      socket.removeAllListeners()
      socket.disconnect()
    }
  }, [clearDegradedTimeout, registered, socket, startDegradedTimeout, token])

  function handleEventFromServer(data: unknown) {
    try {
      const event = ServerToClientMessageParser.parse(data)
      if (event.type === 'Error') {
        store.setError(event.code)
      } else {
        handleAssignInternalConversation(event)
        store.handleEvent(event)
        store.setError(undefined)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.error(`Server sent us invalid message.`, e.message)
    }
  }

  function handleAssignInternalConversation(event: {
    id: string
    type: string
  }) {
    if (event.type === 'InternalConversationCreated') {
      const cmd = {
        conversationId: event.id,
        type: 'InternalConversationAssigned',
      }
      // add check that its assigned
      sendCommandWithResponse(cmd)
    }
  }

  function handleSendError(e: unknown) {
    console.error(e)
    if (e instanceof Error) {
      store.setError(`Error sending a command to server: ${e.message}`)
    } else {
      store.setError('Error occurred')
    }
  }

  function sendConversationRequest<T extends ClientToServerEvent>(
    createCommand: (c: Conversation, state: AppState) => T,
  ) {
    const state = store.getSnapshot()
    const conversation = getActiveConversation(state)
    if (!conversation) {
      return
    }
    const cmd = createCommand(conversation, state)

    sendCommandWithResponse(cmd)
      .then(handleEventFromServer)
      .catch(handleSendError)
  }

  function sendCommandWithResponse(data: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!socket.connected) {
        reject(new Error('No connection'))
      }

      socket.emit('command', data, resolve)
    })
  }

  function closeActiveConversation() {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      id: v4(),
      timestamp: new Date().toISOString(),
      type: 'CloseConversation',
    }))
  }

  async function addMessageToActiveConversation(
    content: string,
    files: File[],
  ) {
    showMessageLoadingIndicator(true)
    const attachments = await uploadFiles(import.meta.env.API_URL, token, files)
    sendConversationRequest((c) => {
      sendTypingIndicator(false)
      showMessageLoadingIndicator(false)
      return {
        attachments,
        content,
        conversationId: c.id,
        id: v4(),
        timestamp: new Date().toISOString(),
        type: 'AddChatMessage',
      }
    })
  }

  function changeActiveConversationQueue(queueId: string) {
    sendConversationRequest((c) => {
      sendTypingIndicator(false)
      return {
        conversationId: c.id,
        id: v4(),
        priority: c.priority,
        queueId,
        timestamp: new Date().toISOString(),
        type: 'MoveConversation',
      }
    })
  }

  function assignActiveConversation(clinician: Clinician) {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      id: v4(),
      timestamp: new Date().toISOString(),
      type: 'AssignConversation',
      userId: clinician.id,
    }))
    if (IsNotLoggedInUser(clinician)) {
      setNotification(`Conversation assigned to ${clinician.name}`)
      store.setActiveConversation(undefined)
    }
  }

  function unassignActiveConversation() {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      id: v4(),
      timestamp: new Date().toISOString(),
      type: 'UnassignConversation',
    }))
  }

  function setNotification(value?: string) {
    store.setNotification(value)
  }

  function changeReasonForEntry(reasonForEntryId: string) {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      id: v4(),
      reasonForEntryId,
      timestamp: new Date().toISOString(),
      type: 'ChangeReasonForEntry',
    }))
  }

  function sendTypingIndicator(isTyping: boolean) {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      id: v4(),
      isTyping,
      timestamp: new Date().toISOString(),
      type: 'SendTypingIndicator',
    }))
  }

  function showMessageLoadingIndicator(isLoading: boolean) {
    const state = store.getSnapshot()
    const conversation = getActiveConversation(state)
    if (!conversation) {
      return
    }
    store.setIsAttachmentLoading({
      conversationId: conversation.id,
      isLoading,
    })
  }

  const throttledSendTypingIndicator = throttle(
    () => sendTypingIndicator(true),
    1,
  )

  function IsNotLoggedInUser(clinician: Clinician) {
    const state = store.getSnapshot()
    return clinician.id !== state.user.id
  }

  function createInternalConversation(clinician: Clinician) {
    sendConversationRequest((c) => ({
      assignedToId: clinician.id,
      conversationId: c.id,
      type: 'CreateInternalConversation',
    }))
  }

  function closeInternalConversation(internalConversationId: string) {
    sendConversationRequest((c) => ({
      conversationId: c.id,
      internalConversationId: internalConversationId,
      type: 'CloseInternalConversation',
    }))
  }

  async function addInternalMessage(
    internalConversationId: string,
    content: string,
    files: File[],
  ) {
    const attachments = await uploadFiles(import.meta.env.API_URL, token, files)
    sendConversationRequest((c) => ({
      attachments,
      content,
      conversationId: c.id,
      internalConversationId,
      type: 'AddInternalChatMessage',
    }))
  }

  return (
    <SocketContext.Provider
      value={{
        addInternalMessage,
        addMessageToActiveConversation,
        assignActiveConversation,
        changeActiveConversationQueue,
        changeReasonForEntry,
        closeActiveConversation,
        closeInternalConversation,
        connectionStatus,
        createInternalConversation,
        reconnect,
        setNotification,
        socket,
        throttledSendTypingIndicator,
        unassignActiveConversation,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
